import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdSlot from '../components/AdSlot';
import styles from './Home.module.css';
import { geocode } from '../api/client';

const RADII = [1, 2, 5, 10, 25];

export default function Home() {
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [radius, setRadius] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!location.trim()) { setError('Please enter a location.'); return; }
    setError('');
    setLoading(true);
    try {
      const geo = await geocode(location);
      navigate(`/results?lat=${geo.lat}&lng=${geo.lng}&radius=${radius}&date=${date || today}&location=${encodeURIComponent(location)}`);
    } catch {
      setError('Could not find that location. Try a city name or zip code.');
    } finally {
      setLoading(false);
    }
  }

  function handleGeolocate() {
    if (!navigator.geolocation) { setError('Geolocation is not supported by your browser.'); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        navigate(`/results?lat=${lat}&lng=${lng}&radius=${radius}&date=${date || today}&location=My+Location`);
      },
      () => setError('Location access denied. Please enter your location manually.')
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h1 className={styles.headline}>Find live music<br />near you tonight.</h1>
        <p className={styles.sub}>Local bars and small venues — the shows that don't make the big sites.</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.locationRow}>
            <input
              className={styles.locationInput}
              type="text"
              placeholder="City, zip, or address"
              value={location}
              onChange={e => setLocation(e.target.value)}
              aria-label="Location"
              autoComplete="off"
            />
            <button type="button" className={styles.geoBtn} onClick={handleGeolocate} title="Use my location" aria-label="Use my location">
              📍
            </button>
          </div>

          <div className={styles.dateRow}>
            <label className={styles.label}>Date</label>
            <input
              className={styles.dateInput}
              type="date"
              value={date}
              min={today}
              max={maxDate}
              onChange={e => setDate(e.target.value)}
              aria-label="Date"
            />
            <button type="button" className={[styles.todayBtn, !date ? styles.todayActive : ''].join(' ')} onClick={() => setDate('')}>
              Today
            </button>
          </div>

          <div className={styles.radiusRow}>
            <label className={styles.label}>Radius</label>
            <div className={styles.radiusBtns}>
              {RADII.map(r => (
                <button
                  key={r}
                  type="button"
                  className={[styles.radiusBtn, r === radius ? styles.radiusActive : ''].join(' ')}
                  onClick={() => setRadius(r)}
                  aria-pressed={r === radius}
                >
                  {r} mi
                </button>
              ))}
            </div>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button type="submit" className={styles.searchBtn} disabled={loading}>
            {loading ? 'Searching…' : 'Find Shows'}
          </button>
        </form>
      </div>

      <div className={styles.adRow}>
        <AdSlot type="leaderboard" />
        <AdSlot type="inline" className="ad-leaderboard-mobile" />
      </div>
    </div>
  );
}
